import { Ship, Navigation, Anchor, FileText, Container, Radio, ArrowUpRight, Users } from "lucide-react";

export const SHIP_STATUSES = ["Booked", "Departed", "In transit", "Arrived port", "Customs cleared", "Unloaded"];
export const stColor = (s) => ({ "Booked": "#64748B", "Departed": "#2563EB", "In transit": "#7C3AED", "Arrived port": "#B4791A", "Customs cleared": "#0891B2", "Unloaded": "#059669" }[s] || "#64748B");
export const GOLD = "#C8A96A";
const NAVY = "#0E1A33";
const NOISE = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

// A shipment is "delivered" (archived) once it has been unloaded.
export const isArchivedShipment = (s) => s.status === "Unloaded";
// Physical containers on a shipment: FCX consolidated containers, otherwise the
// tracked container plus any extra reference containers on the same HBL.
export const shipContainerCount = (s) => {
  const actual = (s.containers?.length || 0) || (s.container_no ? 1 : 0);
  return Math.max(actual, Number(s.expected_containers) || 0);
};
export const shipTitle = (s) => (s.ship_type === "FCX" ? (s.supplier || s.vessel_name || s.hbl || "Consolidated shipment") : (s.supplier || s.vessel_name || s.hbl || "Shipment"));

// Dark "fleet deck" — the overview strip. Deep navy, grain, champagne accents.
export function ShipmentsOverview({ rows }) {
  const n = (s) => rows.filter((r) => r.status === s).length;
  const docs = rows.reduce((a, r) => a + (r.docs?.length || 0), 0);
  const containers = rows.reduce((a, r) => a + shipContainerCount(r), 0);
  const nextEta = rows.map((r) => r.tracking?.eta || r.eta).filter(Boolean).map((d) => new Date(d)).filter((d) => !isNaN(d) && d >= new Date()).sort((a, b) => a - b)[0];
  const tiles = [
    { label: "Shipments", value: rows.length, icon: Ship },
    { label: "Containers", value: containers, icon: Container },
    { label: "On the water", value: n("In transit") + n("Departed"), icon: Navigation },
    { label: "At / near port", value: n("Arrived port") + n("Customs cleared"), icon: Anchor },
    { label: "Documents", value: docs, icon: FileText },
  ];
  return (
    <section className="relative mb-8 overflow-hidden rounded-[28px] text-white shadow-[0_30px_60px_-30px_rgba(14,26,51,0.6)]" style={{ background: NAVY }} data-testid="shipments-overview">
      <div className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{ backgroundImage: NOISE }} />
      <div className="pointer-events-none absolute -right-24 -top-32 h-80 w-80 rounded-full opacity-30 blur-3xl" style={{ background: GOLD }} />
      <div className="pointer-events-none absolute -bottom-40 -left-20 h-80 w-80 rounded-full bg-[#7C3AED] opacity-25 blur-3xl" />
      <div className="relative px-6 pb-6 pt-6 sm:px-9 sm:pb-8 sm:pt-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="h-px w-8" style={{ background: GOLD }} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ color: GOLD }}>Fleet overview</span>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-white/55">
            {nextEta && <span>Next arrival <b className="font-semibold text-white/90">{nextEta.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</b></span>}
            <span className="flex items-center gap-1.5"><span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" /></span>Live via Terminal49</span>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-y-6 sm:grid-cols-3 lg:grid-cols-5 lg:divide-x lg:divide-white/10">
          {tiles.map((t, i) => (
            <div key={t.label} className="animate-fade-up lg:px-6 lg:first:pl-0 lg:last:pr-0" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-end gap-3">
                <span className="font-display text-[2.6rem] font-light leading-none tracking-tight tabular-nums">{t.value}</span>
                <t.icon className="mb-1.5 h-4 w-4 text-white/35" />
              </div>
              <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">{t.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Rough journey % from the live/manual status so a row can show progress without opening it.
const PROG = [
  [/deliver|unload|empty.?return|gate.?out.*(dest|import)/i, 100],
  [/customs|clear/i, 90],
  [/arriv|discharge|destination|import|pod/i, 82],
  [/transit|water|sea|sailing|underway|on board/i, 58],
  [/depart|load|origin|sail|export|pol/i, 35],
  [/book|gate.?in|received|empty|pending/i, 12],
];
export function shipProgress(s) {
  const st = (s.tracking?.status || s.status || "").toString();
  for (const [re, p] of PROG) if (re.test(st)) return p;
  return 8;
}
const fmtDay = (d) => { try { return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return String(d); } };
const relShort = (d) => { try { const n = Math.round((new Date(d) - new Date()) / 86400000); if (n === 0) return "arriving today"; return n > 0 ? `in ${n} day${n > 1 ? "s" : ""}` : `${-n} day${-n > 1 ? "s" : ""} ago`; } catch { return ""; } };

function StatusLabel({ status }) {
  const c = stColor(status);
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: c }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c, boxShadow: `0 0 0 3px ${c}22` }} />{status}
    </span>
  );
}

function RouteLine({ pol, pod, pct, color }) {
  return (
    <div className="mt-3 max-w-xl">
      <div className="relative h-px bg-slate-200">
        <div className="absolute inset-y-0 left-0 transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}66, ${color})` }} />
        <span className="absolute left-0 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-slate-300" />
        <span className="absolute right-0 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full border border-slate-300 bg-white" />
        <span className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pct}%` }}>
          <span className="grid h-6 w-6 place-items-center rounded-full border bg-white shadow-sm" style={{ borderColor: color, color }}><Ship className="h-3 w-3" /></span>
        </span>
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-3 text-[11px]">
        <span className="truncate font-medium text-ink">{pol || "Origin"}</span>
        <span className="truncate text-right font-medium text-ink">{pod || "Destination"}</span>
      </div>
    </div>
  );
}

// Editorial wide row — the shipment list item. Generous spacing, hairline borders,
// large ETA, kinetic route line. Keeps data-testid="vessel-row-{id}".
export function ShipmentCard({ s, onClick, index = 0 }) {
  const isFcx = s.ship_type === "FCX";
  const title = shipTitle(s);
  const actualCont = (s.containers?.length || 0) || (s.container_no ? 1 : 0);
  const contCount = Math.max(actualCont, Number(s.expected_containers) || 0);
  const pendingCont = contCount > actualCont;
  const eta = s.tracking?.eta || s.eta;
  const delay = s.tracking?.delay_days != null ? Number(s.tracking.delay_days) : null;
  const pol = s.tracking?.pol || s.pol;
  const pod = s.tracking?.pod || s.pod;
  const vessel = s.tracking?.vessel || s.vessel_name;
  const docsN = s.docs?.length || 0;
  const color = stColor(s.status);
  const pct = Math.max(4, Math.min(100, shipProgress(s)));
  const meta = [
    vessel && `Vessel ${vessel}`,
    isFcx && s.suppliers?.length ? `${s.suppliers.length} suppliers` : null,
    s.hbl && `HBL ${s.hbl}`,
  ].filter(Boolean);
  return (
    <button onClick={onClick} data-testid={`vessel-row-${s.id}`} style={{ animationDelay: `${index * 50}ms` }}
      className="group relative w-full animate-fade-up overflow-hidden rounded-[22px] border border-slate-200/70 bg-white text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C8A96A]/60 hover:shadow-[0_24px_50px_-24px_rgba(14,26,51,0.35)]">
      <span className="absolute inset-y-5 left-0 w-[3px] rounded-r-full" style={{ background: color }} />
      <div className="grid gap-5 px-6 py-5 sm:grid-cols-[1fr_auto] sm:gap-8 sm:px-8 sm:py-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="font-display text-xl font-semibold tracking-tight text-ink sm:text-[1.35rem]">{title}</span>
            <span className={`rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${isFcx ? "border-[#7C3AED]/30 text-[#7C3AED]" : "border-slate-200 text-slate-500"}`}>{s.ship_type || "FCL"}</span>
            <StatusLabel status={s.status} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {s.container_no && <span className="font-mono tracking-wide text-ink/80">{s.container_no}</span>}
            {meta.map((m) => <span key={m} className="flex items-center gap-1 before:h-0.5 before:w-0.5 before:rounded-full before:bg-slate-300">{m}</span>)}
            {meta.length === 0 && !s.container_no && <span>Awaiting details</span>}
          </div>
          <RouteLine pol={pol} pod={pod} pct={pct} color={color} />
        </div>

        <div className="flex items-end justify-between gap-6 border-t border-slate-100 pt-4 sm:min-w-[11rem] sm:flex-col sm:items-end sm:border-t-0 sm:pt-0">
          <div className="sm:text-right">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Arrives</div>
            <div className="mt-1 font-display text-2xl font-light leading-none tracking-tight text-ink sm:text-[1.9rem]">{eta ? fmtDay(eta) : "—"}</div>
            {eta && (
              <div className={`mt-1.5 text-[11px] font-semibold ${delay > 0 ? "text-error" : delay < 0 ? "text-[#059669]" : new Date(eta) < new Date() ? "text-muted-foreground" : ""}`} style={!delay && new Date(eta) >= new Date() ? { color: GOLD } : undefined}>
                {delay != null && delay !== 0 ? (delay > 0 ? `${delay}d behind schedule` : `${-delay}d ahead of schedule`) : relShort(eta)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><Container className="h-3 w-3" />{contCount}{pendingCont && <span className="ml-0.5 rounded-sm bg-[#B4791A]/10 px-1 text-[9px] font-semibold text-[#B4791A]" data-testid={`numbers-pending-${s.id}`}>pending</span>}</span>
            <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{docsN}</span>
            {isFcx && s.suppliers?.length ? <span className="flex items-center gap-1"><Users className="h-3 w-3" />{s.suppliers.length}</span> : null}
            <span className="grid h-7 w-7 place-items-center rounded-full border border-slate-200 text-slate-400 transition-all duration-300 group-hover:border-[#C8A96A] group-hover:bg-[#C8A96A] group-hover:text-white"><ArrowUpRight className="h-3.5 w-3.5" /></span>
          </div>
        </div>
      </div>
    </button>
  );
}

// Compact row used by the customs-agent dashboard.
export function ShipmentRow({ s, onClick }) {
  const actualCont = (s.containers?.length || 0) || (s.container_no ? 1 : 0);
  const contCount = Math.max(actualCont, Number(s.expected_containers) || 0);
  const pendingCont = contCount > actualCont;
  const isFcx = s.ship_type === "FCX";
  const eta = s.tracking?.eta || s.eta;
  const meta = [
    isFcx && s.suppliers?.length ? `${s.suppliers.length} suppliers` : null,
    s.hbl && `HBL ${s.hbl}`,
    contCount > 1 ? `${contCount} containers` : s.container_no,
  ].filter(Boolean).join(" · ");
  return (
    <button onClick={onClick} data-testid={`vessel-row-${s.id}`} className="group relative flex w-full items-center justify-between gap-4 overflow-hidden rounded-2xl border border-slate-200/70 bg-white px-5 py-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C8A96A]/60 hover:shadow-[0_20px_40px_-24px_rgba(14,26,51,0.35)]">
      <span className="absolute inset-y-4 left-0 w-[3px] rounded-r-full" style={{ background: stColor(s.status) }} />
      <div className="min-w-0 pl-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="truncate font-display text-base font-semibold tracking-tight text-ink">{shipTitle(s)}</span>
          <span className={`rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${isFcx ? "border-[#7C3AED]/30 text-[#7C3AED]" : "border-slate-200 text-slate-500"}`}>{s.ship_type || "FCL"}</span>
          <StatusLabel status={s.status} />
        </div>
        <div className="mt-1 truncate text-xs text-muted-foreground">{meta || "Awaiting details"}</div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {pendingCont && <span className="rounded-sm bg-[#B4791A]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#B4791A]" data-testid={`numbers-pending-${s.id}`}>numbers pending</span>}
        {s.docs?.length ? <span className="hidden items-center gap-1 text-[11px] text-muted-foreground sm:flex"><FileText className="h-3 w-3" />{s.docs.length}</span> : null}
        <div className="text-right">
          <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Arrives</div>
          <div className="font-display text-lg font-light leading-none text-ink">{eta ? fmtDay(eta) : "—"}</div>
        </div>
      </div>
    </button>
  );
}

export { Radio };
